import React from "react";
import { Card, Table, Tag } from "antd";
import { AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

// =============================================================================
// READ-ONLY PlanFeatures
//
// Props:
//   allFeatures    — full feature list from fetchFeatures API (array)
//   userFeatureIds — userData.features e.g. "1,2,5,6,16,20"  (comma-separated string)
//   isLoading      — boolean
//
// Logic:
//   - Parse userFeatureIds → Set of enabled IDs
//   - Filter allFeatures: those whose id is in the set → ENABLED
//   - All others → DISABLED
//   - Sort by feature_order
// =============================================================================
const PlanFeatures = ({ allFeatures = [], userFeatureIds = "", isLoading = false }) => {
  // Parse "1,2,5,6,16,20" → [1, 2, 5, 6, 16, 20]
  const enabledIds = new Set(
    userFeatureIds
      .toString()
      .split(",")
      .map((id) => parseInt(id.trim(), 10))
      .filter(Boolean)
  );

  // console.log("from plan-features", allFeatures);

  // Only show features the user actually has (filter by their IDs)
  // Sort by feature_order
  const userFeatures = [...allFeatures]
    .filter((f) => enabledIds.has(f.id))
    .sort((a, b) => (a.feature_order ?? 0) - (b.feature_order ?? 0))
    .map((f) => ({ ...f, is_enabled: true }));

  // For display stats
  const enabledCount  = userFeatures.length;
  const disabledCount = 0;

  // Alias for table rendering
  const featuresWithStatus = userFeatures;

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "#",
      key: "idx",
      width: 50,
      align: "center",
      render: (_, __, i) => (
        <span style={{ color: "#8c8c8c", fontWeight: 600 }}>{i + 1}</span>
      ),
    },
    {
      title: "Feature",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: record.is_enabled ? "#262626" : "#bfbfbf",
            }}
          >
            {text}
          </div>
          {record.feature_key !== "tm_document_size" && (
            <>
              <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                {record.description}
              </div>
              {record.feature_type === "numeric" && record.is_enabled && (
                <div style={{ fontSize: 11, color: "#1677ff", marginTop: 2, fontWeight: 600 }}>
                  Default limit: {record.default_value}
                </div>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_enabled",
      key: "is_enabled",
      align: "center",
      width: 140,
      render: (enabled) =>
        enabled ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            ENABLED
          </Tag>
        ) : (
          <Tag
            icon={<CloseCircleOutlined />}
            color="default"
            style={{ opacity: 0.5 }}
          >
            DISABLED
          </Tag>
        ),
    },
  ];

  return (
    <Card
      title={
        <div className="d-flex align-items-center gap-2">
          <AppstoreOutlined style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600 }}>Plan Features</span>
          <Tag color="blue" style={{ marginLeft: 4 }}>
            {featuresWithStatus.length}
          </Tag>
        </div>
      }
      className="shadow-sm mb-4"
      style={{ borderRadius: 12 }}
    >
      {featuresWithStatus.length > 0 ? (
        <>
          <Table
            columns={columns}
            dataSource={featuresWithStatus.map((f) => ({ ...f, key: f.id }))}
            pagination={false}
            size="middle"
            bordered
            loading={isLoading}
            rowClassName={(record) => (!record.is_enabled ? "feature-row-disabled" : "")}
          />

          {/* Stats bar */}
          <div
            className="mt-3 p-3 rounded"
            style={{
              background: "linear-gradient(135deg,#f0f7ff 0%,#e8f4fd 100%)",
              border: "1px solid #bae0ff",
            }}
          >
            <div className="row text-center g-0">
              <div className="col-12">
                <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>
                  {enabledCount}
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Active Features</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <AppstoreOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />
          <p className="text-muted mt-3">
            {isLoading ? "Loading features…" : "No features assigned to this user."}
          </p>
        </div>
      )}

      <style>{`
        .feature-row-disabled td {
          opacity: 0.45;
        }
      `}</style>
    </Card>
  );
};

export default PlanFeatures;
